import { z } from "zod";
import type { ClinicalPhase } from "./clinical-phase";
import {
  treatmentStartPrecisionSchema,
  type TreatmentStartPrecision,
} from "./prescription-transfer-review";

// TES-1398 (2.1 slice S4; Emelie Ö-420, Ö-421, TES-1396) — a prescription
// transfer's treatment start on the doctor's patient view, after the
// approval that first recorded it (TES-734):
//
//   "Startdatumet för patientens nuvarande sammanhängande behandlingsperiod
//    anges i F-07 och verifieras eller korrigeras av läkaren. Det styr både
//    behandlingens dagräkning och vilket DT-innehåll patienten ansluter till."
//
// The doctor sees whether the start is exact or estimated, which treatment
// day and DT that gives her today, and may correct it. A correction moves the
// count from today on; DT she has not been served is never published
// retroactively.
//
// GET/PUT employee-api/v1/patients/:patientId/transfer-treatment-start.
// Doctor only.

export const setTransferTreatmentStartSchema = z.object({
  startedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date_required"),
  precision: treatmentStartPrecisionSchema,
});
export type SetTransferTreatmentStartRequest = z.infer<
  typeof setTransferTreatmentStartSchema
>;

export interface TransferTreatmentStartView {
  // A prescription-transfer patient whose transfer was approved: the start
  // can be shown and corrected. False for everyone else, and the other
  // fields are then null.
  applicable: boolean;
  // Treatment day 1, a calendar date. Null when no start was recorded (a
  // transfer approved before TES-734): she then has no day count at all.
  startedOn: string | null;
  precision: TreatmentStartPrecision | null;
  clinicalPhase: ClinicalPhase | null;
  // The day she became active with Ella (approved transfer and a verified
  // subscription), or null while she has not.
  joinedOn: string | null;
  // Her treatment day today. Null without a start, before she has joined,
  // or outside ACTIVE_TREATMENT (weight stabilisation has no daily count).
  treatmentDay: number | null;
  // The DT she is served today: her treatment day while it is 1..60, null
  // from day 61 (the weekly surface) and whenever treatmentDay is null.
  dtDay: number | null;
  // The first DT she was or will be served: her treatment day on the day she
  // joined. Earlier DT is never shown to her.
  firstDtDay: number | null;
}
